import { captureException } from '@sentry/react';
import toast from 'react-hot-toast';

import { Card } from 'components/Card';

import { FileUploaderButton } from 'src/app/document/components/FileUploaderButton';
import { getDisplayError } from '../../../../../utils/get-display-error';
import { nullthrows } from '../../../../../utils/invariant';
import { LANGUAGE_VALUES } from '../../../../../utils/lang';
import { useAllGeneralSettings } from '../../../all-settings-context';
import type { LanguageValue } from '../../../endpoints/shared.types';
import { linkCarrierTermsDocument, unlinkCarrierTermsDocument } from '../../../models';

/**
 * General terms & conditions (incl. the self-billing procedure) that carriers have
 * to accept before they can confirm a transport order — one document per language.
 * Linked from the acceptance dialog and attached to the transport order / planning
 * emails in the contact's language, falling back to EN and then NL.
 */
export const CarrierTermsSection = () => {
  const { settings, refreshData } = useAllGeneralSettings();

  return (
    <Card
      title="Algemene voorwaarden vervoerders"
      subtitle="Vervoerders met self billing krijgen deze voorwaarden bij de transport order- en planning-e-mails en aanvaarden ze bij het bevestigen. Ontbreekt een taal, dan gelden EN en daarna NL."
    >
      <div className="flex flex-wrap gap-2">
        {LANGUAGE_VALUES.map(({ key: language }) => {
          const terms = settings.carrierTermsDocuments.find((t) => t.language === language);

          return (
            <FileUploaderButton
              key={language}
              buttonText={`Voorwaarden ${language}`}
              title={`Algemene voorwaarden vervoerders - ${language}`}
              initialName={`Algemene voorwaarden ${language}`}
              file={terms?.document}
              onSubmit={async (document) => {
                await linkCarrierTermsDocument({ documentId: document.id, language: language as LanguageValue });
                refreshData();
              }}
              unlinkDocument={async () => {
                try {
                  await unlinkCarrierTermsDocument(nullthrows(terms, 'Terms document not found').id);
                  refreshData();
                  toast.success('Algemene voorwaarden gewist');
                } catch (err) {
                  captureException(err);
                  toast.error('Kon algemene voorwaarden niet wissen: ' + getDisplayError(err));
                }
              }}
            />
          );
        })}
      </div>
    </Card>
  );
};
